import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Download, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

export default function MyPurchases() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPurchases = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/purchases`);
      setPurchases(response.data);
    } catch {
      // Purchases fetch failed — page shows empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPurchases();
  }, [authLoading, isAuthenticated, navigate, fetchPurchases]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#71717A]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Your Collection</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#F5F5F0]">
            My Purchases
          </h1>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-square skeleton" />
            ))}
          </div>
        ) : purchases.length === 0 ? (
          <div className="text-center py-24">
            <Package className="mx-auto text-[#71717A] mb-6" size={48} />
            <p className="text-[#71717A] text-lg mb-8">No purchases yet</p>
            <Link to="/gallery" className="btn-primary inline-block">
              Explore Gallery
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="purchases-grid">
            {purchases.map(purchase => (
              <div
                key={purchase.id}
                className="bg-[#121212] border border-white/5 overflow-hidden"
                data-testid={`purchase-${purchase.id}`}
              >
                {purchase.artwork && (
                  <>
                    <div className="aspect-square relative group">
                      <img
                        src={purchase.artwork.image_url}
                        alt={purchase.artwork.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                        <a
                          href={purchase.artwork.image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F5F5F0] transition-colors"
                          title="Download"
                        >
                          <Download size={20} />
                        </a>
                        <Link
                          to={`/artwork/${purchase.artwork.id}`}
                          className="p-3 bg-white/20 text-[#F5F5F0] hover:bg-white/30 transition-colors"
                          title="View Details"
                        >
                          <Eye size={20} />
                        </Link>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <h3 className="font-serif text-lg text-[#F5F5F0]">
                        {purchase.artwork.title}
                      </h3>
                      <p className="text-[#71717A] text-sm">
                        by {purchase.artwork.artist_name}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-[#D4AF37]">
                          ${purchase.price?.toFixed(2)}
                        </span>
                        <span className="text-[#71717A] text-xs">
                          {new Date(purchase.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
